import React, { useEffect, useState } from "react";
import {
  MdGroups,
  MdBadge,
  MdAdminPanelSettings,
  MdSend,
  MdRefresh,
  MdWarningAmber,
  MdSchedule,
  MdMailOutline,
  MdKey,
  MdSettings,
} from "react-icons/md";
import { toast } from "react-toastify";
import Card from "../../../components/card";
import api from "../../../services/axios";
import GoalsSettings from "./components/GoalsSettings";
import CopilotAdminCard from "../../../components/copilot/CopilotAdminCard";
import { mensagemDoServidor } from "../../../utils/mensagemDoServidor";

type Audience = "clients" | "employees" | "managers";

interface ReportSchedule {
  audience: string; // "Clients" | "Employees" | "Managers"
  enabled: boolean;
  dayOfWeek: number; // 0=Dom..6=Sáb
  hour: number; // 0-23
}

const WEEKDAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const schedSelectCls =
  "rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const AUDIENCE_LABEL: Record<string, string> = {
  Clients: "Clientes",
  Employees: "Colaboradores",
  Managers: "Gestores e Admin",
};

interface EmailSettingsStatus {
  configured: boolean;
  hasResendKey: boolean;
  maskedResendKey?: string | null;
  resendFrom?: string | null;
  source: string; // "env/appsettings" | "portal" | "none"
  keyFromConfig: boolean;
  smtpConfigured: boolean;
}

interface ReportLogItem {
  id: number;
  recipientType: string;
  recipientName: string;
  email?: string | null;
  periodStart: string;
  periodEnd: string;
  totalHours: number;
  success: boolean;
  errorMessage?: string | null;
  sentAt: string;
}

const AUDIENCES: { key: Audience; title: string; desc: string; icon: JSX.Element }[] = [
  {
    key: "clients",
    title: "Clientes",
    desc: "Envia o report de horas da última semana aos clientes com recebimento ativo.",
    icon: <MdGroups className="h-6 w-6" />,
  },
  {
    key: "employees",
    title: "Colaboradores",
    desc: "Envia a cada colaborador o resumo das próprias horas da última semana.",
    icon: <MdBadge className="h-6 w-6" />,
  },
  {
    key: "managers",
    title: "Gestores e Admin",
    desc: "Envia um report consolidado do time (todos os clientes/colaboradores) aos gestores e admins.",
    icon: <MdAdminPanelSettings className="h-6 w-6" />,
  },
];

const SettingsPage: React.FC = () => {
  const [sending, setSending] = useState<Audience | null>(null);
  const [log, setLog] = useState<ReportLogItem[]>([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState<boolean | null>(null);
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [savingSchedules, setSavingSchedules] = useState(false);
  const [emailSettings, setEmailSettings] = useState<EmailSettingsStatus | null>(null);
  const [resendKeyInput, setResendKeyInput] = useState("");
  const [resendFromInput, setResendFromInput] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const loadLog = () => {
    setLoadingLog(true);
    api
      .get("/Financial/WeeklyReportLog", { params: { take: 50 } })
      .then((r) => setLog(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Erro ao carregar histórico de reports", e))
      .finally(() => setLoadingLog(false));
  };

  useEffect(() => {
    loadLog();
    api
      .get("/Financial/EmailStatus")
      .then((r) => setEmailConfigured(!!r.data?.configured))
      .catch(() => setEmailConfigured(null));
    api
      .get("/Financial/ReportSchedules")
      .then((r) => setSchedules(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Erro ao carregar agendamentos", e));
    loadEmailSettings();
  }, []);

  const loadEmailSettings = () => {
    api
      .get("/Financial/EmailSettings")
      .then((r) => {
        setEmailSettings(r.data);
        setResendFromInput(r.data?.resendFrom ?? "");
      })
      .catch((e) => console.error("Erro ao carregar configuração de e-mail", e));
  };

  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await api.put("/Financial/EmailSettings", {
        resendApiKey: resendKeyInput.trim() || undefined,
        resendFrom: resendFromInput,
      });
      toast.success("Configuração de e-mail salva.");
      setResendKeyInput("");
      loadEmailSettings();
      const st = await api.get("/Financial/EmailStatus");
      setEmailConfigured(!!st.data?.configured);
    } catch (e: any) {
      console.error("Erro ao salvar configuração de e-mail", e);
      toast.error(
        e?.response?.status === 403
          ? "Apenas Admin pode alterar a chave de e-mail."
          : mensagemDoServidor(e, "Erro ao salvar configuração de e-mail.")
      );
    } finally {
      setSavingEmail(false);
    }
  };

  const updateSchedule = (audience: string, patch: Partial<ReportSchedule>) =>
    setSchedules((prev) => prev.map((s) => (s.audience === audience ? { ...s, ...patch } : s)));

  const saveSchedules = async () => {
    setSavingSchedules(true);
    try {
      await api.put("/Financial/ReportSchedules", { schedules });
      toast.success("Agendamento salvo.");
    } catch (e) {
      console.error("Erro ao salvar agendamento", e);
      toast.error("Erro ao salvar agendamento.");
    } finally {
      setSavingSchedules(false);
    }
  };

  const sendReport = async (audience: Audience, label: string) => {
    if (!window.confirm(`Enviar o report da última semana para ${label}?`)) return;
    setSending(audience);
    try {
      const { data } = await api.post("/Financial/SendReports", null, { params: { audience } });
      const count =
        audience === "clients"
          ? data?.clientEmailsSent
          : audience === "employees"
          ? data?.employeeEmailsSent
          : data?.managerEmailsSent;
      if (data?.emailConfigured === false) {
        toast.warning(
          `Processados ${count ?? 0} destinatário(s), mas o SMTP não está configurado — os e-mails NÃO foram entregues.`
        );
      } else {
        toast.success(`Report enviado para ${label}: ${count ?? 0} e-mail(s).`);
      }
      loadLog();
    } catch (e: any) {
      console.error("Erro ao enviar report", e);
      toast.error(mensagemDoServidor(e, "Erro ao enviar report."));
    } finally {
      setSending(null);
    }
  };

  const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
  const fmtDateTime = (iso?: string) =>
    iso ? new Date(iso).toLocaleString("pt-BR") : "—";

  return (
    <div className="mt-5 flex animate-fade-up flex-col gap-4">
      {/* Cabeçalho */}
      <div className="flex items-center gap-4">
        <span
          className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
          aria-hidden="true"
        >
          <MdSettings className="h-6 w-6" />
        </span>
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
            Configurações
          </span>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
            Metas, reports e e-mail
          </h1>
        </div>
      </div>

      {emailConfigured === false && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-300">
          <MdWarningAmber className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-semibold">Envio de e-mail não configurado (modo de simulação)</p>
            <p className="text-xs">
              Nenhum provedor de e-mail está definido (Resend ou SMTP), então os e-mails são apenas
              registrados no log e <strong>não são entregues</strong>. Informe a{" "}
              <strong>API key do Resend</strong> no card abaixo para habilitar o envio real.
            </p>
          </div>
        </div>
      )}

      {/* Metas da empresa — alimentam os KPIs da home */}
      <GoalsSettings />

      {/* Manutenção do RAG da NINA (só Admin). Veio da home: é rotina de infraestrutura,
          não indicador — a tela inicial existe para decidir, não para acionar backfill. */}
      <CopilotAdminCard />

      {/* Provedor de e-mail (Resend) */}
      <Card extra="p-5">
        <div className="flex items-center gap-2">
          <MdMailOutline className="h-5 w-5 text-dv-blue-500 dark:text-dv-cyan-300" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">Provedor de e-mail (Resend)</span>
        </div>
        <h3 className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
          Chave de envio de e-mail
        </h3>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              emailSettings?.configured
                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
            }`}
          >
            {emailSettings?.configured ? "Configurado" : "Não configurado"}
          </span>
          {emailSettings?.hasResendKey && (
            <span className="text-xs text-gray-700 dark:text-gray-300">
              Chave atual: <code>{emailSettings.maskedResendKey}</code>
              {emailSettings.source === "env/appsettings"
                ? " (via variável de ambiente)"
                : emailSettings.source === "portal"
                ? " (definida no portal)"
                : ""}
            </span>
          )}
        </div>

        {emailSettings?.keyFromConfig && (
          <p className="mt-2 rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            Há uma chave definida por variável de ambiente/appsettings, que tem precedência. O valor
            salvo aqui só será usado se a variável de ambiente for removida.
          </p>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor="resendApiKey" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              API key do Resend
            </label>
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 transition-all focus-within:border-dv-blue-500 focus-within:ring-2 focus-within:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900">
              <MdKey className="h-4 w-4 text-gray-400" aria-hidden="true" />
              <input
                id="resendApiKey"
                type="password"
                autoComplete="off"
                placeholder={emailSettings?.hasResendKey ? "•••• (manter atual)" : "re_xxxxxxxxxxxx"}
                value={resendKeyInput}
                onChange={(e) => setResendKeyInput(e.target.value)}
                className="w-full bg-transparent py-2.5 text-sm text-gray-800 outline-none dark:text-white"
              />
            </div>
            <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-300">
              Deixe em branco para manter a chave atual. Gere em resend.com → API Keys.
            </p>
          </div>
          <div>
            <label htmlFor="resendFrom" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
              Remetente (From)
            </label>
            <input
              id="resendFrom"
              type="text"
              placeholder="NINA <no-reply@seudominio.com>"
              value={resendFromInput}
              onChange={(e) => setResendFromInput(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-300">
              Use um domínio verificado no Resend (ou onboarding@resend.dev só para testes).
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={saveEmailSettings}
            disabled={savingEmail}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            {savingEmail ? "Salvando..." : "Salvar chave"}
          </button>
          <span className="text-[11px] text-gray-700 dark:text-gray-300">
            A chave é guardada no servidor (banco) e nunca é exibida por completo. Apenas Admin pode alterá-la.
          </span>
        </div>
      </Card>

      <Card extra="p-5">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">Envio manual</span>
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">Envio de reports</h3>
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
          Dispare manualmente os e-mails de report da última semana fechada (segunda a domingo).
        </p>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {AUDIENCES.map((a) => (
            <div
              key={a.key}
              className="group relative flex flex-col gap-3 overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-dv-md dark:border-white/10 dark:bg-navy-900"
            >
              <span
                className="pointer-events-none absolute -right-10 -top-12 h-24 w-24 rounded-full bg-dv-blue-500/10 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                aria-hidden="true"
              />
              <div className="relative flex items-start gap-3">
                <span
                  className="dv-aura flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
                  aria-hidden="true"
                >
                  {a.icon}
                </span>
                <div className="min-w-0">
                  <p className="font-bold text-navy-700 dark:text-white">{a.title}</p>
                  <p className="mt-0.5 text-xs text-gray-700 dark:text-gray-300">{a.desc}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => sendReport(a.key, a.title)}
                disabled={sending !== null}
                className="relative mt-auto flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-4 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
              >
                <MdSend className="h-4 w-4" aria-hidden="true" />
                {sending === a.key ? "Enviando…" : "Enviar report"}
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Agendamento automático */}
      <Card extra="p-5">
        <div className="mb-1 flex items-center gap-2">
          <MdSchedule className="h-5 w-5 text-dv-blue-500 dark:text-dv-cyan-300" aria-hidden="true" />
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Agendamento automático</h3>
        </div>
        <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
          Defina para quem o report é enviado automaticamente e quando. O sistema verifica de hora em hora.
        </p>

        <div className="flex flex-col gap-3">
          {schedules.length === 0 ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">Carregando agendamentos…</p>
          ) : (
            schedules.map((s) => (
              <div
                key={s.audience}
                className="flex flex-col gap-3 rounded-2xl border border-gray-100 p-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    aria-label="Ativar/desativar"
                    aria-pressed={s.enabled}
                    onClick={() => updateSchedule(s.audience, { enabled: !s.enabled })}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                      s.enabled ? "bg-dv-blue-500" : "bg-gray-300 dark:bg-gray-600"
                    }`}
                  >
                    <span
                      className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        s.enabled ? "translate-x-5" : ""
                      }`}
                    />
                  </button>
                  <div>
                    <p className="font-semibold text-navy-700 dark:text-white">
                      {AUDIENCE_LABEL[s.audience] ?? s.audience}
                    </p>
                    {s.audience === "Clients" && (
                      <p className="text-[11px] text-gray-700 dark:text-gray-300">
                        Cada cliente segue o próprio dia/hora; aqui só liga/desliga o envio automático.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={s.dayOfWeek}
                    onChange={(e) => updateSchedule(s.audience, { dayOfWeek: Number(e.target.value) })}
                    disabled={!s.enabled || s.audience === "Clients"}
                    className={schedSelectCls}
                    aria-label="Dia da semana"
                  >
                    {WEEKDAYS.map((d, i) => (
                      <option key={i} value={i}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={s.hour}
                    onChange={(e) => updateSchedule(s.audience, { hour: Number(e.target.value) })}
                    disabled={!s.enabled || s.audience === "Clients"}
                    className={schedSelectCls}
                    aria-label="Hora"
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                    ))}
                  </select>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={saveSchedules}
            disabled={savingSchedules || schedules.length === 0}
            className="rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            {savingSchedules ? "Salvando…" : "Salvar agendamento"}
          </button>
        </div>
      </Card>

      <Card extra="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Histórico de envios</h3>
          <button
            type="button"
            onClick={loadLog}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            <MdRefresh className="h-4 w-4" aria-hidden="true" /> Atualizar
          </button>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-900">
                {["Tipo", "Destinatário", "E-mail", "Período", "Horas", "Status", "Enviado em"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingLog ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700 dark:text-gray-300">
                    Carregando…
                  </td>
                </tr>
              ) : log.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-navy-900 dark:text-gray-500">
                      <MdMailOutline className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <p className="text-sm font-semibold text-navy-700 dark:text-white">
                      Nenhum envio registrado
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      Os reports enviados aparecerão aqui assim que o primeiro disparo acontecer.
                    </p>
                  </td>
                </tr>
              ) : (
                log.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-navy-700"
                  >
                    <td className="px-4 py-2.5">{item.recipientType}</td>
                    <td className="px-4 py-2.5 font-medium text-navy-700 dark:text-white">
                      {item.recipientName}
                    </td>
                    <td className="px-4 py-2.5">{item.email ?? "—"}</td>
                    <td className="px-4 py-2.5 tabular-nums">
                      {fmtDate(item.periodStart)} – {fmtDate(item.periodEnd)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{item.totalHours}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          item.success
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        }`}
                        title={item.errorMessage ?? ""}
                      >
                        {item.success ? "Enviado" : "Falha"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{fmtDateTime(item.sentAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default SettingsPage;
